import debounce from 'lodash/debounce'
import get from 'lodash/get'
import React, { useEffect, useMemo, useRef } from 'react'
import Select, { CSSObjectWithLabel, InputProps, StylesConfig } from 'react-select'
import useRecordSearch from '../../../hooks/useRecordSearch'
import { eventTimeZone, formatShortDate } from '../../../util/dateTime'
import { recordTown } from '../../../util/recordTown'
import { IconGlyph } from '../../../icons/IconGlyph'

type Option = { value: string | number; icon: React.ReactNode | null; label: string }

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const RecordSearchAutocomplete: React.FC = ({ onChange, name, value, searchProps, ...selectProps }) => {
  const {
    loading,
    componentProps: { searchRecords, data, communityId, recordsType },
  } = useRecordSearch(searchProps)

  // One debounced search for the component's life, reading the latest search inputs. A new
  // debounce per render let a re-render mid-typing (the form's duplicate check answering, say)
  // leave the old one pending, which then searched for a partial term and replaced the options.
  const latestSearchRef = useRef({ searchRecords, communityId, recordsType })
  latestSearchRef.current = { searchRecords, communityId, recordsType }
  const onInputChange = useMemo(
    () =>
      debounce((newValue: string) => {
        if (newValue.length < 3) {
          return
        }
        const latest = latestSearchRef.current
        latest.searchRecords({
          variables: { searchTerm: newValue, communityId: latest.communityId, recordsType: latest.recordsType },
        })
      }, 350),
    [],
  )
  useEffect(() => () => onInputChange.cancel(), [onInputChange])

  const records = get(data, 'search', [])
  const options = records.map(r => {
    let icon
    switch (r.__typename) {
      case 'Event':
        icon = <IconGlyph name="calendar" color="red" />
        break
      case 'Place':
        icon = <IconGlyph name="location" color="green" />
        break
      case 'Story':
        icon = <IconGlyph name="megaphone" color="blue" />
        break
      default:
        icon = null
        break
    }
    let label = r.title || r.name
    if (r.startAt) {
      label += ` (${formatShortDate(r.startAt, eventTimeZone(r))})`
    }
    // A record found outside the chosen community carries its own community's name, so ten
    // "Town Hall"s can be told apart.
    const recordCommunities = r.communities || []
    if (communityId && recordCommunities.length > 0 && !recordCommunities.some(c => c.id === communityId)) {
      label += ` (${recordTown(recordCommunities)?.name})`
    }
    return { value: r.id, icon, label }
  })

  // Reset input styles so global form input rules don't draw an inner box.
  const { styles: userStyles, ...restSelectProps } = selectProps as { styles?: StylesConfig<Option, false> }
  const mergedStyles: StylesConfig<Option, false> = {
    ...userStyles,
    input: (provided: CSSObjectWithLabel, state: InputProps<Option, false>) => {
      const base = {
        ...provided,
        border: 0,
        boxShadow: "none",
        padding: 0,
        margin: 0,
      }
      return userStyles?.input ? userStyles.input(base, state) : base
    },
  }

  return (
    <Select
      name={name}
      isLoading={loading}
      value={value}
      classNamePrefix="cl-select"
      onChange={x => {
        const selectedRecord = x ? records.find(r => r.id === x.value) : null
        onChange(selectedRecord)
      }}
      onInputChange={onInputChange}
      formatOptionLabel={option => (
        <>
          {option.icon} {option.label}
        </>
      )}
      placeholder="Start typing to search..."
      isClearable
      styles={mergedStyles}
      {...restSelectProps}
      options={selectProps.options || options}
    />
  )
}

export default RecordSearchAutocomplete
